import { cn } from '@/utils';

export interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => React.ReactNode;
  className?: string;
  /**
   * Column width, used only when the parent `Table` has `layout="fixed"`.
   * Any valid CSS width ('120px', '10rem', '20%', ...). Applied via a
   * `<colgroup>` rather than a Tailwind class because widths here are
   * per-page, arbitrary values a caller computes at runtime -- Tailwind's
   * JIT scanner only picks up class names that appear literally in source,
   * so a templated `w-[${width}]` would silently fail to generate.
   *
   * Ignored when layout is 'auto' (the default): table-auto derives column
   * widths from cell content, so a fixed `width` here would be misleading.
   *
   * THIS WIDTH INCLUDES THE CELL PADDING. Cells are `box-sizing: border-box`
   * (Tailwind preflight) and this component pads every `<td>` with `px-6` --
   * 48px -- so the space left for content is `width - 48px`, and content
   * wider than that is CLIPPED (`truncate` sets `overflow: hidden`). A cell
   * holding an interactive control must therefore be sized as
   * `control width + 48px`, or the padding must be overridden through
   * `className` (`cn` is tailwind-merge, so a `px-*` here beats the `px-6`).
   */
  width?: string;
  /**
   * U-04: render this column's header as an interactive sort control.
   *
   * Deliberately opt-IN per column. This project already shipped the
   * opposite (every header looked clickable, most did nothing) and the fix
   * was to flip the flag to opt-in -- do not regress that. A column with
   * `sortable: true` is still inert unless the table is also given an
   * `onSortChange` handler.
   */
  sortable?: boolean;
}

export type SortDirection = 'asc' | 'desc';

export interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  isLoading?: boolean;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
  rowKey: (item: T) => string;
  /**
   * G2 ("columns shift when filters are applied"): every one of this app's
   * hand-rolled `<table>`s uses the browser default `table-auto`, which
   * recomputes every column's width from its content on each render --
   * so changing the filtered/sorted rows reflows the whole table, which is
   * exactly the complaint.
   *
   * 'auto' (the default) keeps that existing behaviour byte-for-byte, so
   * every current caller of this component is unaffected.
   *
   * 'fixed' switches the table to `table-fixed` and reads each column's
   * width from `Column.width` via a `<colgroup>`. This is opt-in rather
   * than the new default because `table-fixed` with no widths supplied
   * collapses every column to an equal share of the table's width, which
   * can look worse than `table-auto` for a table that hasn't been given
   * widths yet. A page adopting `layout="fixed"` should set `width` on
   * most/all of its columns at the same time.
   */
  layout?: 'auto' | 'fixed';
  /**
   * Truncate long cell content with an ellipsis (and a `title` tooltip for
   * plain text/number content) instead of letting it wrap or force the
   * column wider. Only meaningful with `layout="fixed"` -- `table-auto`
   * already sizes columns to fit content, so there is nothing to truncate.
   * Defaults to `true` whenever `layout="fixed"`.
   */
  truncateCells?: boolean;
  /**
   * U-02: stick the header row to the top of the table's scroll container
   * while the body scrolls under it. Defaults to false, so no existing
   * caller changes.
   *
   * A `position: sticky` header only sticks against a scroll container with
   * a *bounded* height; the wrapper today is `overflow-x-auto` with no
   * height limit, so the page scrolls rather than the table and nothing ever
   * sticks. Setting `stickyHeader` therefore also bounds the wrapper --
   * `maxHeight` when given, otherwise `65vh`.
   */
  stickyHeader?: boolean;
  /**
   * Bound the table's scroll container to this CSS height (any valid CSS
   * length: '400px', '60vh', ...). Applied as an inline style rather than a
   * Tailwind class because the value is arbitrary and per-page, and
   * Tailwind's JIT scanner only sees class names that appear literally in
   * source.
   *
   * Usable on its own (a scrollable table with a normal header) or together
   * with `stickyHeader`. Undefined by default, which is today's behaviour.
   */
  maxHeight?: string;
  /** U-04: key of the column currently sorted, if any. */
  sortKey?: string;
  /** U-04: direction of the current sort. Ignored unless `sortKey` matches a column. */
  sortDirection?: SortDirection;
  /**
   * U-04: called when a sortable header is activated, with the next sort
   * state: a newly chosen column starts at 'asc'; the already-active column
   * toggles 'asc' <-> 'desc'. Sorting itself stays with the caller (most of
   * this app sorts server-side), so `Table` never reorders `data`.
   *
   * Without this handler no header renders as a button, however many columns
   * are marked `sortable`.
   */
  onSortChange?: (key: string, direction: SortDirection) => void;
  /**
   * Tighter cell padding (px-3 instead of px-6) for lists with many columns, so an
   * eight-column admin list fits a 1440px screen instead of scrolling sideways (R2-F4).
   * Off by default, so no existing caller changes. Applies to header and body cells.
   */
  compact?: boolean;
}

/**
 * Directional indicator for the active sorted column.
 *
 * Two idioms exist in this codebase: AdminCrudPage appends a literal arrow
 * character to the header text, and VehicleDatabasePage renders an icon with
 * an aria-label. The icon wins here: a literal arrow inside the header text
 * breaks `getByText('Name')` for every consumer's tests and is announced as
 * a bare glyph, while the icon stays out of the header's text content.
 *
 * The icon is PURELY visual (`aria-hidden`). The sort state is announced once,
 * by the `aria-sort` on the `<th>` -- that is the standard ARIA mechanism for
 * a sortable column header, and assistive tech reads it as part of the column
 * header. Giving the icon an aria-label instead folds the state into the
 * *button's* accessible name ("Name sorted ascending"), so the name changes as
 * the user toggles and the state is announced twice. The visual affordance is
 * unchanged; only the duplicate announcement is gone.
 */
function SortIndicator({ direction }: { direction: SortDirection }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      data-testid="sort-indicator"
      data-direction={direction}
      className="h-3 w-3 shrink-0 text-gray-500 dark:text-gray-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d={direction === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
      />
    </svg>
  );
}

export function Table<T>({
  data,
  columns,
  isLoading,
  emptyMessage = 'No data available',
  onRowClick,
  rowKey,
  layout = 'auto',
  truncateCells = layout === 'fixed',
  stickyHeader = false,
  maxHeight,
  sortKey,
  sortDirection,
  onSortChange,
  compact = false,
}: TableProps<T>) {
  const isFixed = layout === 'fixed';
  // Sorting is only interactive when someone is listening.
  const sortingEnabled = Boolean(onSortChange);
  const boundedHeight = maxHeight ?? (stickyHeader ? '65vh' : undefined);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-100 dark:bg-gray-700" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 border-t border-gray-100 dark:border-gray-700 flex items-center px-6">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-12">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
          <p className="mt-4">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div
        className={cn('overflow-x-auto', boundedHeight && 'overflow-y-auto')}
        style={boundedHeight ? { maxHeight: boundedHeight } : undefined}
      >
        {/*
          `w-full` is NOT redundant next to `min-w-full`, and it is what makes
          `table-fixed` do anything at all. `table-layout: fixed` is INERT
          while the table's used width is auto: CSS2.1 17.5.2.1 only applies
          the fixed algorithm when 'width' has a computed value other than
          'auto', and `min-w-full` sets a MINIMUM, not a width -- so Chromium
          falls straight back to the auto algorithm and re-derives every
          column from the rows currently on screen, which is the column-jump
          complaint (U-02/G2) verbatim. With a width set, the declared
          `<colgroup>` widths are used as-is and the table's own used width
          becomes max(100% of the wrapper, sum of the column widths), so a
          declared set wider than the viewport scrolls inside the card's
          `overflow-x-auto` instead of squeezing the columns.

          Scoped to `isFixed`, so no `layout="auto"` caller changes.
        */}
        <table
          className={cn(
            'min-w-full divide-y divide-gray-200 dark:divide-gray-700',
            isFixed && 'w-full table-fixed'
          )}
        >
          {isFixed && (
            <colgroup>
              {columns.map((column) => (
                <col key={column.key} style={column.width ? { width: column.width } : undefined} />
              ))}
            </colgroup>
          )}
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              {columns.map((column) => {
                const isSortable = Boolean(column.sortable) && sortingEnabled;
                const isActive = isSortable && sortKey === column.key;
                const activeDirection: SortDirection = sortDirection ?? 'asc';
                const truncateHeader = isFixed && truncateCells;
                return (
                  <th
                    key={column.key}
                    title={truncateHeader ? column.header : undefined}
                    aria-sort={
                      isSortable ? (isActive ? (activeDirection === 'asc' ? 'ascending' : 'descending') : 'none') : undefined
                    }
                    className={cn(
                      compact ? 'px-3 py-3' : 'px-6 py-3',
                      'text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider',
                      // An opaque background is required on the sticky cells
                      // themselves: the thead's dark background is
                      // translucent, so rows would show through it.
                      stickyHeader && 'sticky top-0 z-10 bg-gray-50 dark:bg-gray-700',
                      truncateHeader && 'truncate',
                      column.className
                    )}
                  >
                    {isSortable ? (
                      <button
                        type="button"
                        onClick={() =>
                          onSortChange!(
                            column.key,
                            isActive && activeDirection === 'asc' ? 'desc' : 'asc'
                          )
                        }
                        // `max-w-full` lets the button shrink to the column
                        // instead of overflowing it: a sortable header is an
                        // inline-flex box, whose shrink-to-fit width would
                        // otherwise be capped only by its own content.
                        className="group inline-flex max-w-full items-center gap-1 uppercase tracking-wider hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                      >
                        {/*
                          The ellipsis must live on the element that actually
                          holds the text. `text-overflow` applies to a block
                          container's inline content, never to an atomic
                          inline-level box -- so `truncate` on the <th> around
                          this button hard-CLIPS the label with no ellipsis.
                          As a flex item this span is blockified, and its
                          `overflow: hidden` zeroes the automatic minimum size
                          so it can shrink below its content width.
                        */}
                        <span className={cn(truncateHeader && 'truncate')}>{column.header}</span>
                        {isActive && <SortIndicator direction={activeDirection} />}
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {data.map((item) => (
              <tr
                key={rowKey(item)}
                className={cn(
                  'hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors',
                  onRowClick && 'cursor-pointer'
                )}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((column) => {
                  const content = column.render
                    ? column.render(item)
                    : (item as Record<string, unknown>)[column.key]?.toString() ?? '-';
                  // A title/tooltip only makes sense for content we can render
                  // as text -- a custom `render()` can return arbitrary JSX
                  // (badges, links, icons), and stringifying that would show
                  // "[object Object]" on hover, which is worse than no tooltip.
                  const isPlainText = typeof content === 'string' || typeof content === 'number';
                  return (
                    <td
                      key={`${rowKey(item)}-${column.key}`}
                      title={isFixed && truncateCells && isPlainText ? String(content) : undefined}
                      className={cn(
                        // 48px of horizontal padding. A `width` a caller
                        // declares for this column has to carry it -- see
                        // `Column.width`. Overridable per column via
                        // `Column.className` (tailwind-merge).
                        compact ? 'px-3 py-4' : 'px-6 py-4',
                        'text-sm dark:text-gray-300',
                        isFixed && truncateCells ? 'truncate' : 'whitespace-nowrap',
                        column.className
                      )}
                    >
                      {content}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

/** A slot in the page strip: a page to offer, or a run of pages it skips. */
type PageSlot = number | 'gap-before' | 'gap-after';

/**
 * B-01: how many pages either side of the current one the strip offers.
 *
 * Five numbered pages around the current one, plus the first and the last,
 * which is enough to step a couple of pages by hand and to jump to either end
 * without the strip growing with the book.
 */
const PAGE_WINDOW_RADIUS = 2;

/**
 * B-01: the page strip for `currentPage`, as a sliding window.
 *
 * What this replaces: `[...Array(Math.min(totalPages, 5))]` with `page = i + 1`,
 * i.e. pages 1..5 and nothing else, on all ~47 call sites. Past page 5 no
 * button matched the current page so NOTHING was highlighted, and pages 6+
 * could only be reached by clicking Next once per page.
 *
 * An ellipsis is only inserted where it actually hides more than one page --
 * when exactly one page falls outside the window that page is rendered
 * instead, because a "…" standing in for a single page costs a click and
 * saves nothing. `currentPage` is clamped first: a caller holding page 7 of a
 * list that has just been filtered down to 2 pages renders for one frame
 * before its own reset lands, and an unclamped window there would offer pages
 * that 404.
 */
function pageWindow(currentPage: number, totalPages: number): PageSlot[] {
  if (totalPages < 1) return [];
  if (totalPages === 1) return [1];

  const current = Math.min(Math.max(currentPage, 1), totalPages);
  const first = Math.max(2, current - PAGE_WINDOW_RADIUS);
  const last = Math.min(totalPages - 1, current + PAGE_WINDOW_RADIUS);

  const slots: PageSlot[] = [1];
  if (first > 2) slots.push(first === 3 ? 2 : 'gap-before');
  for (let page = first; page <= last; page += 1) slots.push(page);
  if (last < totalPages - 1) {
    slots.push(last === totalPages - 2 ? totalPages - 1 : 'gap-after');
  }
  slots.push(totalPages);
  return slots;
}

export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
}: PaginationProps) {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const slots = pageWindow(currentPage, totalPages);

  // Disabled by RANGE, not by equality. `currentPage === totalPages` left Next
  // live whenever a caller's page number had overshot the page count (a filter
  // that shrinks the book while the user sits on page 7), so the one control
  // that must never page off the end was the one that could.
  const onFirstPage = currentPage <= 1;
  const onLastPage = currentPage >= totalPages;

  const edgeButton =
    'relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 sm:px-6">
      {/* Desktop only: the item range. The controls below are NOT inside this
          -- the whole pager used to be `hidden sm:flex`, which left a phone
          with no way at all to leave page 1. */}
      <p className="hidden sm:block text-sm text-gray-600 dark:text-gray-300">
        Showing <span className="font-medium">{startItem}</span> to{' '}
        <span className="font-medium">{endItem}</span> of{' '}
        <span className="font-medium">{totalItems}</span> results
      </p>
      <nav
        aria-label="Pagination"
        className="flex flex-1 items-center justify-between gap-2 sm:flex-none sm:justify-end sm:gap-0 sm:rounded-md sm:shadow-sm"
      >
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={onFirstPage}
          className={cn(edgeButton, 'sm:rounded-r-none')}
        >
          Previous
        </button>

        {/* Phone: the page strip would not fit, so the position is stated
            instead of offered as buttons. Prev/Next still work, which is what
            "hidden sm:flex" took away. */}
        <span className="text-sm text-gray-600 dark:text-gray-300 sm:hidden">
          Page {Math.min(Math.max(currentPage, 1), Math.max(totalPages, 1))} of{' '}
          {Math.max(totalPages, 1)}
        </span>

        <div className="hidden sm:inline-flex sm:-space-x-px sm:-ml-px">
          {slots.map((slot) =>
            typeof slot === 'number' ? (
              <button
                key={slot}
                onClick={() => onPageChange(slot)}
                // The active page is announced, not merely coloured: a colour
                // change is nothing to a screen reader, and this pager spent
                // its whole life unable to highlight anything past page 5.
                aria-current={slot === currentPage ? 'page' : undefined}
                className={cn(
                  'relative inline-flex items-center px-4 py-2 text-sm font-medium border',
                  slot === currentPage
                    ? 'z-10 bg-primary-50 border-primary-500 text-primary-600 dark:bg-primary-900/30 dark:border-primary-500 dark:text-primary-400'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
                )}
              >
                {slot}
              </button>
            ) : (
              <span
                key={slot}
                aria-hidden="true"
                className="relative inline-flex items-center px-4 py-2 text-sm font-medium border bg-white border-gray-300 text-gray-400 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-500"
              >
                …
              </span>
            )
          )}
        </div>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={onLastPage}
          className={cn(edgeButton, 'sm:rounded-l-none sm:-ml-px')}
        >
          Next
        </button>
      </nav>
    </div>
  );
}
